import { sortBy, uniqBy } from "lodash";
import { action, computed, observable } from "mobx";
import {
  isPickupFiltered,
  isStopFiltered,
  isFuturePickup,
  isTodaysPickup
} from "../components/modules/Dispatch/RouteExecution/filterUtils";
import { format, isSameDay } from "date-fns";
import appConstants from "../appConstants";
import {
  isDriverReadyForDispatch,
  isWithinTime,
  isDriverTimeBeforeCurrentDateAndTime,
  updateDriverListByEventMode
} from "../components/modules/Dispatch/RouteExecution/utils";
import { utcToZonedTime } from "date-fns-tz";
import {
  getPickupTimeToCloseInMins,
  getDuplicatePickupsByPickupId,
  isPossibleMergeFilterApplied,
  isActivePickup,
  getTotalInTrailer,
} from "../components/modules/Dispatch/utils/utils";
import { createGroupedData, isPickupGroupHeader, isGroupHeaders } from "../components/modules/Dispatch/RouteExecution/Pickups/GroupPickup/utils";

class DispatchStore {
  //original data Sources
  @observable pickupsList = [];
  @observable filteredPickupsOnMap = [];
  @observable filteredDriversOnMap = [];
  @observable currentTabName = "";
  @observable selectedRouteNameList = [];
  @observable selectedPickupIdList = [];
  @observable filteredStopsList = [];
  @observable stopsFilter = [];
  @observable stopsFilterData = [];
  @observable tractors = [];
  @observable pickupsFilter = "";
  @observable driverUnReadMessageCountList = [];
  @observable selectedDriverRouteId = null;
  @observable selectedDispatchDriverIdList = [];
  @observable availableDriverList = [];
  @observable dispatchedDrivers = [];
  @observable terminals = [];
  @observable pickupRestrictions = null;
  @observable driversRouteGridMessage = "loading";
  @observable pickupsGridMessage = "loading";
  @observable driversGridMessage = "loading";
  @observable dispatchGridMessage = "loading";
  @observable staticClientData = null;
  @observable hazmatInfo = null;
  @observable showAllDrivers = true;
  @observable showDriverLabel = true;
  @observable expandDriverStopList = false;
  @observable terminalOffset = null;
  @observable closingSoonWarningTimeLimit= null;
  @observable closingSoonAlertTimeLimit= null;
  @observable dispatchedDriverNotifications = [];
  @observable movePickupTerminalList = [];
  @observable duplicatePickups = [];
  @observable driverLocations = {};
  @observable geofenceWindow = {};
  @observable selectedStopInKPI = null;
  @observable pickupIdToClosingTimeMapping = {};
  @observable showOnHoverPopup = true;
  @observable routeMappingData = [];
  @observable showReturnReasonCode = true;

  @action setShowReturnReasonCode(value) {
    this.showReturnReasonCode = Boolean(value);
  }

  @action addDispatchedDriverNotification(messages) {
    this.dispatchedDriverNotifications.replace([
      ...messages,
      ...this.dispatchedDriverNotifications,
    ]);
  }

  @action updateNotificationReadStatus(driverId) {
    const updatedMessages = this.dispatchedDriverNotifications.filter(msg=>
      msg.sender.toUpperCase() !== driverId.toUpperCase()
    );
    this.dispatchedDriverNotifications.replace(updatedMessages);
  }

  @action addPickups(pickupsList) {
    //added orderby so that current date(Active) pickup can come on top in unassign and all grid filter
    const groupedData = createGroupedData(pickupsList);
    this.pickupsList.replace(groupedData);
    this.updateFilteredPickups(groupedData);
    this.updateClosingSoonPickups();

    //Reset selected pickups if pickups list is empty
    const isEmptyPickupsList = this.pickupsList?.length === 0;
    if (isEmptyPickupsList) {
      this.updateSelectedPickups([]);
    }
  }

  @action setTabName(name) {
    this.currentTabName = name;
  }
  @action setTerminals(terminals) {
    this.terminals.replace(terminals);
  }

  @action setPickupRestrictions(pickupRestrictions) {
    this.pickupRestrictions = pickupRestrictions;
  }

  @action setTractors(tractors) {
    this.tractors.replace(tractors);
  }

  @action setAvailableDriverList(driverList) {
    this.availableDriverList.replace(driverList);
  }

  @action updateAvailableDriverByEvent(signalRData) {
    const { updatedDriverList, isDriverListModified } = updateDriverListByEventMode(
      this.availableDriverList.slice(),
      signalRData
    );
    if (isDriverListModified) {
      this.availableDriverList.replace(updatedDriverList);
    }
  }

  @action setTerminalOffset(terminalOffset) {
    this.terminalOffset = terminalOffset;
  }

  @action setClosingSoonTimeThresholds(closingSoonWarningTimeLimit,closingSoonAlertTimeLimit) {
    this.closingSoonWarningTimeLimit = closingSoonWarningTimeLimit;
    this.closingSoonAlertTimeLimit = closingSoonAlertTimeLimit;
  }

  @action updatePickupsFilter(option) {
    this.pickupsFilter = option;
  }

  @action updateMapFilter(stopFilter) {
    this.stopsFilter.replace(stopFilter);
  }

  @action addDriverLocation(driversLocationMap) {
    let driversLocationToBeUpdated = {};
    driversLocationMap.forEach((eventData, driverId) => {
      const { eventTime, lat, lng } = eventData;
      const lastConnectedTime =
        eventTime?.length > 5
          ? format(
              utcToZonedTime(eventTime, this.terminalOffset),
              appConstants.DATETYPES.TIME.format
            )
          : eventTime;
      driversLocationToBeUpdated[driverId.toUpperCase()] = {
        lastConnectedTime,
        lat,
        lng
      };
    });
    this.driverLocations = {
      ...this.driverLocations,
      ...driversLocationToBeUpdated
    };
  }

  @action addDispatchedDrivers(dispatchedDrivers, routeColorList) {
    let colorIndex = -1;
    this.driverLocations = {};
    this.geofenceWindow ={};
    const driversLocationMap = new Map();

    this.dispatchedDrivers.replace(
      dispatchedDrivers.map(
        ({
          lastKnownLatitude,
          lastKnowLongitude,
          lastConnectedTime,
          ...driver
        }) => {
          colorIndex++;
          driversLocationMap.set(driver.driverId, {lat: lastKnownLatitude, lng: lastKnowLongitude, eventTime: lastConnectedTime});
          return {
            ...driver,
            unReadCount: this.getUnreadMessageCounts(driver.driverId),
            dispatchRoutes:
              driver.dispatchRoutes?.map((route) => ({
                ...route,
                totalWeight: getTotalInTrailer(route.stops, "weight"),
                color: routeColorList[colorIndex % routeColorList.length],
                stops: route.stops.map(({geoFenceArrivalTime, geoFenceDepartureTime, ...stop}) => {
                  if(geoFenceArrivalTime || geoFenceDepartureTime){
                    this.updateGeofenceArrivalDeparture({
                      driverId: driver.driverId,
                      stopId: stop.stopId,
                      geoFenceArrivalTime,
                      geoFenceDepartureTime
                    })
                  }
                  return stop;
                })
              })) ?? [],
          };
        }
      )
    );

    this.updateFilteredDrivers();
    this.addDriverLocation(driversLocationMap);
  }

  @action updateSelectedPickups(selectedPickupIds) {
    this.selectedPickupIdList.replace(
      selectedPickupIds.map((pickupId) => pickupId.toLowerCase())
    );
  }

  @action updateSelectedRoutes(routeIdList) {
    this.selectedRouteNameList.replace(routeIdList);
  }

  @action updateSelectedDispatchDriver(driverIdList) {
    this.selectedDispatchDriverIdList.replace(driverIdList);
  }

  @computed get filteredPickups() {
    const isPossibleMergeFilter = isPossibleMergeFilterApplied(
      this.pickupsFilter.value
    );
    return this.filteredPickupsOnMap.filter((pickup) =>
      isPickupFiltered({
        pickup,
        ...(isPossibleMergeFilter && {
          duplicatePickups: getDuplicatePickupsByPickupId(
            this.duplicatePickups,
            pickup.pickupId
          ),
        }),
        filters: [this.pickupsFilter],
        terminalOffset: this.terminalOffset,
      })
    );
  }

  @action updateFilteredPickups(filteredData) {
    if (filteredData) {
      this.filteredPickupsOnMap.replace(filteredData);
    } else {
      this.filteredPickupsOnMap.replace(this.pickupsList);
    }
  }

  @action setStaticClientData(clientData) {
    this.staticClientData = clientData;
  }

  @action getStaticClientData() {
    return this.staticClientData;
  }

  @action setRouteMappingData(routeMappingData) {
    this.routeMappingData.replace(routeMappingData);
  }

  @computed get returnCodeForReturnTerminal() {
    return this.returnCodes.filter(
      (returnCode) => returnCode.displayReturnToTerminal
    );
  }

  @computed get pickupAccessorialList() {
    return this.staticClientData?.clientCodes?.accessorialList?.filter(
      (data) => data.type === appConstants.PICKUP_ACCESSORIAL_CODE
    ) || [];
  }

  @action setHazmatInfo(hazmatInfo) {
    this.hazmatInfo = hazmatInfo;
  }

  @computed get hazmatInfoList() {
    return this.hazmatInfo;
  }

  @computed get deliveryAccessorialList() {
    return this.staticClientData?.clientCodes?.accessorialList?.filter(
      (data) => data.type === appConstants.DELIVERY_ACCESSORIAL_CODE
    ) || [];
  }

  @computed get tractorList() {
    return this.tractors.map((tractor) => ({
      label: tractor.tractorNumber.toString(),
      labelSuffix: tractor.isCityTrailer ? "(City)" : "(Linehaul)",
      value: tractor.tractorNumber,
    }));
  }

  @computed get dispatchRouteList() {
    return this.routesList
      .filter((route) => route.routeId)
      .map((route) => ({
        label: route.routeName,
        value: route.routeId,
      }));
  }

  @computed get dispatchedDriversList() {
    return this.dispatchedDrivers.map((driver) => ({
      label: `#${driver.driverNumber}`,
      value: driver.driverId,
      labelSuffix: `(${driver.driverName})`
    }));
  }

  @computed get activeDispatchedDriversList() {
    return uniqBy(
      this.currentActiveRoutes.map((driver) => ({
        label: `#${driver.driverNumber}`,
        value: driver.driverId,
        labelSuffix: `(${driver.driverName})`,
        driverNumber: driver.driverNumber,
        driverName: driver.driverName
      })),
      'value'
    );
  }

  @computed get activeDriversList() {
    const activeDriversList = this.routesList.map((driver) => ({
      label: driver.driverName,
      value: driver.driverId,
      labelSuffix: `(${driver.driverNumber})`
    }));
    return [
      ...new Map(
        activeDriversList.map((driver) => [driver.value, driver])
      ).values(),
    ];
  }

  @action updateMultiplePickups(updatedPickupsList){
      const updatedPickups = updatedPickupsList.reduce(
        (pickupList, updatedPickup) => {
          const existingIndex = pickupList.findIndex((pickup)=>
            updatedPickup.pickupId.toUpperCase() === pickup.pickupId.toUpperCase()
          );
          if (existingIndex !== -1) {
            pickupList[existingIndex] = { ...pickupList[existingIndex], ...updatedPickup };
          } else {
            pickupList = [{ ...updatedPickup }, ...pickupList];
          }
          return pickupList;
        },
        [...this.pickupsList]
      );
      this.addPickups(updatedPickups);
  }

  @action updatedSinglePickupInList = (pickupData, updatedPickupId) => {
    const pickupIndex = this.pickupsList.findIndex(
      (pickup) => pickup.pickupId?.toUpperCase() === updatedPickupId?.toUpperCase()
    );
    if (pickupIndex === -1 && pickupData) {
      //if index not match will push pickup info in a list :add
      this.addPickups([{ routeId: null, ...pickupData }, ...this.pickupsList]);
    } else if (!pickupData && pickupIndex >= 0 ) {
      //if there is no data and index match will remove that pickup from list : remove
      this.addPickups(
        this.pickupsList.filter(
          (pickup) => pickup.pickupId?.toUpperCase() !== updatedPickupId?.toUpperCase()
        )
      );
      // Remove the updated pickup from selectedPickupIdList also.
      this.updateSelectedPickups(
        this.selectedPickupIdList.filter(
          (pickupId) => pickupId?.toUpperCase() !== updatedPickupId?.toUpperCase()
        )
      );
    } else if (pickupData && pickupIndex >= 0 ) {
      // if index match and data is there will update pickup in list : update
      this.updateMultiplePickups([pickupData]);
    }
  };
  
  @action updateDispatchedDrivers(
    updatedDispatchedDrivers,
    updatedDrivers,
    routeColorList
  ) {
    const driversLocationMap = new Map();
    // if drivers routes are completed by doing end trip, they are no longer updated so we need to remove them from the list
    const updatedDispatchedDriversList = updatedDispatchedDrivers.map(
      (driver) => driver.driverId.toUpperCase()
    );

    const currentDispatchedDrivers = this.dispatchedDrivers.map((d) =>
      d.driverId.toUpperCase()
    );

    const driversToAdd = updatedDispatchedDrivers.filter(
      (driver) =>
        !currentDispatchedDrivers.includes(driver.driverId.toUpperCase())
    );

    const driversToRemove = updatedDrivers
      .map((driverId) => driverId.toUpperCase())
      .filter(
        (driverId) =>
          !updatedDispatchedDriversList.includes(driverId.toUpperCase())
    );

    // remove driverLocations
    driversToRemove.map(
      (driverId) =>{
        delete this.driverLocations[driverId.toUpperCase()]
        this.removeDriverGeofence(driverId)
      } 
    );

    const dispatchedDrivers = this.dispatchedDrivers
      .filter(
        (driver) => !driversToRemove.includes(driver.driverId.toUpperCase())
      )
      .map((driver) => {
        const updateDriverData = updatedDispatchedDrivers.find(
          (updatedDriver) =>
            updatedDriver.driverId.toUpperCase() ===
            driver.driverId.toUpperCase()
        );
        if (updateDriverData) {
          const {
            lastKnownLatitude,
            lastKnowLongitude,
            lastConnectedTime,
            ...updateDriver
          } = updateDriverData;
          driversLocationMap.set(updateDriver.driverId, {lat: lastKnownLatitude, lng: lastKnowLongitude, eventTime: lastConnectedTime});
          return {
            ...updateDriver,
            unReadCount: this.getUnreadMessageCounts(updateDriver.driverId),
            dispatchRoutes:
              updateDriver.dispatchRoutes?.map((route) => ({
                ...route,
                color: driver.dispatchRoutes.find(
                  (r) => r.routeId === route.routeId
                )?.color,
                totalWeight: getTotalInTrailer(route.stops, "weight"),
                stops: route.stops.map(({geoFenceArrivalTime, geoFenceDepartureTime, ...stop}) => {
                  if(geoFenceArrivalTime || geoFenceDepartureTime){
                    this.updateGeofenceArrivalDeparture({
                      driverId: driver.driverId,
                      stopId: stop.stopId,
                      geoFenceArrivalTime,
                      geoFenceDepartureTime
                    })
                  }
                  return stop;
                })
              })) ?? [],
          };
        }
        return driver;
      });
    driversToAdd.map((driver, index) => {
      driversLocationMap.set(driver.driverId, {lat: driver.lastKnownLatitude, lng: driver.lastKnowLongitude, eventTime: driver.lastConnectedTime});
      dispatchedDrivers.push({
        ...driver,
        unReadCount: this.getUnreadMessageCounts(driver.driverId),
        dispatchRoutes:
          driver.dispatchRoutes?.map((route) => ({
            ...route,
            totalWeight: getTotalInTrailer(route.stops, "weight"),
            color:
              routeColorList[
                (dispatchedDrivers.length + index) % routeColorList.length
              ],
          })) ?? [],
      });
    });

    this.dispatchedDrivers.replace(dispatchedDrivers);
    this.addDriverLocation(driversLocationMap);
  }

  @action addUnReadMessageCountToDriver(driverId) {
    const updatedDriversList = this.dispatchedDrivers.map((driver) => {
      if (driver.driverId === driverId) {
        driver["unReadCount"] = driver["unReadCount"] + 1;
        this.updateUnreadMessageCountList(driverId, driver["unReadCount"]);
      }
      return driver;
    });
    this.dispatchedDrivers.replace(updatedDriversList);
  }

  @action clearDriverUnreadCount(driverId) {
    const updatedDriversList = this.dispatchedDrivers.map((driver) => {
      if (driver.driverId === driverId) {
        driver["unReadCount"] = 0;
        this.updateUnreadMessageCountList(driverId, driver["unReadCount"]);
      }
      return driver;
    });
    this.dispatchedDrivers.replace(updatedDriversList);
  }


  @action getDispatchRoute(routeId) {
    return this.routesList.find((route) => route.routeId === routeId);
  }

  @action getDispatchDriverRoute(driverId, routeId) {
    return this.routesList.find(
      (route) =>
        route.driverId?.toUpperCase() === driverId?.toUpperCase() &&
        route.routeId?.toUpperCase() === routeId?.toUpperCase()
    );
  }

  @action getDriverDetails(driverId) {
    let pendingStops = [];
    let color = null;
    this.routesList.forEach((route) => {
      if (route.driverId === driverId) {
        pendingStops = pendingStops.concat(
          route.stops.filter((stop) => !stop.isStopCompleted)
        );
        color = route.color;
      }
    });
    return {
      pendingStops: pendingStops,
      color: color,
    };
  }

  @action getTractor(tractorNumber) {
    return this.tractors.find(
      (tractor) => tractor.tractorNumber === tractorNumber
    );
  }

  @action getSelectedDriverCurrentRoutes() {
    return this.currentActiveRoutes.filter(
      (route) =>
        this.selectedDispatchDriverIdList.includes(route.driverId) &&
        route.dispatchId
    );
  }

  @action showErrorMessageOnGrid(gridName, value) {
    switch (gridName) {
      case "driversRouteGridMessage":
        this.driversRouteGridMessage = value;
        break;
      case "pickupsGridMessage":
        this.pickupsGridMessage = value;
        break;
      case "driversGridMessage":
        this.driversGridMessage = value;
        break;
      case "dispatchGridMessage":
        this.dispatchGridMessage = value;
        break;
      case "all":
        this.dispatchGridMessage = value;
        this.driversRouteGridMessage = value;
        this.pickupsGridMessage = value;
        this.driversGridMessage = value;
        break;
    }
  }
  @action getPickupData(pickupId) {
    return this.pickupsList.find(
      (pickup) => pickup.pickupId?.toLowerCase() === pickupId?.toLowerCase()
    );
  }

  @action setShowAllDrivers(showAllDrivers) {
    this.showAllDrivers = showAllDrivers;
  }

  @action setShowDriverLabel(showDriverLabel) {
    this.showDriverLabel = showDriverLabel;
  }

  @action setExpandDriverStopList(expandDriverStopList) {
    this.expandDriverStopList = expandDriverStopList;
  }

  @action setShowOnHoverPopup(showOnHoverPopup) {
    this.showOnHoverPopup = showOnHoverPopup;
  }

  @action getDriverRoutes(driverId) {
    return this.routesList.filter((route) => route.driverId === driverId);
  }

  @action updatePickupAcknowledgedStatus(routeId, pickupId) {
    const updatedDispatchedDrivers = this.dispatchedDrivers.map((driver) => {
      if (
        driver.dispatchRoutes.findIndex(
          (route) => route.routeId.toLowerCase() === routeId.toLowerCase()
        ) > -1
      ) {
        return {
          ...driver,
          dispatchRoutes: driver.dispatchRoutes.map((route) => {
            if (route.routeId.toLowerCase() === routeId.toLowerCase()) {
              route.stops.map((stop) => {
                if (stop.pickupId?.toLowerCase() === pickupId.toLowerCase()) {
                  stop.isAcknowledged = true;
                }
                return stop;
              });
            }
            return route;
          }),
        };
      }
      return driver;
    });
    const updatedPickupList = this.pickupsList.map((pickup) => {
      if (pickup.pickupId?.toLowerCase() === pickupId.toLowerCase()) {
        pickup.isAcknowledged = true;
      }
      return pickup;
    });
    this.addPickups(updatedPickupList);
    this.dispatchedDrivers.replace(updatedDispatchedDrivers);
  }

  @computed get selectedPickupsList() {
    return this.pickupsList
      .filter((pickup) =>
        this.selectedPickupIdList.includes(pickup.pickupId.toLowerCase())
      )
      .map(
        ({
          pickupId,
          shipperLatitude,
          shipperLongitude,
          shipperAddress,
          driverId,
          routeId,
          isCompleted,
          pickupDate,
          isVoided,
          isMissed,
          groupId,
          terminalCode,
          driverName,
          pickupNumber
        }) => ({
          pickupId,
          position: {
            lat: shipperLatitude,
            lng: shipperLongitude,
          },
          shipperAddress,
          driverId,
          routeId,
          isCompleted,
          isVoided,
          isMissed,
          isFuture: isFuturePickup(pickupDate, this.terminalOffset),
          groupId,
          pickupDate,
          terminalCode,
          driverName,
          pickupNumber
        })
      );
  }

  @computed get selectedGroupPickupsList() {
    // First, find all groups that contain selected pickups
    const selectedGroups = new Set();
    
    this.pickupsList.forEach((pickup) => {
      if (this.selectedPickupIdList.includes(pickup.pickupId.toLowerCase()) && pickup.groupId) {
        selectedGroups.add(pickup.groupId);
      }
    });

    // Create grouped data for all pickups
    const groupedData = createGroupedData(this.pickupsList);
    
    // Return only group headers for the selected groups
    return groupedData.filter(item => 
      isGroupHeaders(item) && selectedGroups.has(item.groupId)
    );
  }

  @computed get dispatchDrivers() {
    return uniqBy(
      this.currentActiveRoutes.map((route) => ({
        label: route.driverName,
        value: route.driverId,
      })),
      "value"
    );
  }

  @computed get routesWithFilteredStops() {
    const selectedRoute =
      this.selectedDriverRouteId &&
      this.routesList.find(
        (route) => route.routeId === this.selectedDriverRouteId
      );
    const routesList = selectedRoute
      ? [selectedRoute]
      : this.currentActiveRoutes;
    return routesList
      .filter((route) => this.selectedRouteNameList.includes(route.routeId))
      .map((route) => {
        const filteredStops = (
          this.stopsFilter.length
            ? route.stops.filter((stop) =>
            isStopFiltered(
              stop,
              this.stopsFilter,
              this.staticClientData?.clientCodes?.serviceLevelList
              )) : []
        ).map((stop) => {
          return {
            ...(stop ?? {}),
            driverName: route.driverName,
            driverNumber: route.driverNumber,
            driverId: route.driverId,
            color: route.color,
          };
        });
        return { ...route, filteredStops };
      });
  }

  @computed get routesList() {
    return this.dispatchedDrivers.reduce((routes, driver) => {
      return [
        ...routes,
        ...(driver.dispatchRoutes?.map((route) => ({
          ...driver,
          dispatchRoutes: undefined,
          ...route,
        })) ?? []),
      ];
    }, []);
  }

  @action getShouldBeVisibleDrivers(availableDriverList = []) {
    const currentUTCDate = utcToZonedTime(new Date(), this.terminalOffset);
    const activeDriverIds = this.dispatchedDrivers.map((driver) => driver.driverId);
    // excluding those already Dispatched
    // including drivers scheduled for the current date within a DRIVER_VISIBLE_TIME time window
    // or driver clocked in before the current date and time.
    return availableDriverList.filter(
      (driver) =>
        !activeDriverIds.includes(driver.driverId) &&
        ((driver.scheduledDate &&
          isSameDay(currentUTCDate, driver.scheduledDate) &&
          isWithinTime(driver.scheduleTime, this.terminalOffset)) ||
          isDriverTimeBeforeCurrentDateAndTime(driver.clockedInTime, this.terminalOffset))
    );
  }

  @computed get currentActiveRoutes() {
    const activeRoutes = this.dispatchedDrivers.map((driver) => {
      const routes = driver.dispatchRoutes ?? [];
      const [allTrailerNumbers, allManifestNumbers] = routes.reduce(
        ([trailers, manifests], route) => {
          if (route.trailerNumber) trailers.push(route.trailerNumber);
          if (route.manifestNumber) manifests.push(route.manifestNumber);
          return [trailers, manifests];
        },
        [[], []]
      );
      return {
        ...driver,
        dispatchRoutes: undefined,
        stops: [],
        ...(driver.dispatchRoutes?.find((route) => route.isActiveRoute) ?? []),
        trailerNumber: allTrailerNumbers.join(", "),
        manifestNumber: allManifestNumbers.join(", "),
      };
    });
    const scheduledDrivers = this.getShouldBeVisibleDrivers(
      this.availableDriverList.slice()
    ).map((driver) => ({
      currentStatus: driver.driverStatus,
      driverId: driver.driverId,
      driverName: driver.driverName,
      driverNumber: driver.driverNumber,
      driverPhoneNumber: driver.phoneNumber,
      driverStatus: driver.driverStatus,
      loggedInStatus: driver.loggedInStatus,
      driverClockedInTime: driver.clockedInTime,
      driverType: driver.driverType,
      stops: [],
      scheduleTime: driver.scheduleTime,
      isCompanyDriver: driver.isCompanyDriver,
      accruedHours: driver.accruedHours,
      driverClockedInDept: driver.clockedInDept,
      delays: driver.delays||[],
      endOfTripTime: driver.endOfTripTime
    }));

    return sortBy(activeRoutes.concat(scheduledDrivers), [
      (item) => !Boolean(item.dispatchId), // Sorts to move already dispatched route to top
      (item) =>
        !isDriverReadyForDispatch({
          driverClockedInTime: item.driverClockedInTime,
          isCompanyDriver: item.isCompanyDriver,
          terminalOffset: this.terminalOffset
        }), // Sorts to move drivers not ready to dispatch to bottom
      (item) => item.driverDispatchTime && new Date(item.driverDispatchTime), // sorts by driverDispatch time
      (item) =>
        isDriverReadyForDispatch({
          driverClockedInTime: item.driverClockedInTime,
          isCompanyDriver: item.isCompanyDriver,
          terminalOffset: this.terminalOffset
        })
          ? item.driverClockedInTime && new Date(item.driverClockedInTime)
          : Number(item.driverNumber) // if isReadyForDispatch sort clockedin time else by driverNumber
    ]);
  }

  @action updateFilteredDrivers(filteredData) {
    if (filteredData) {
      this.filteredDriversOnMap.replace(filteredData);
    } else {
      this.filteredDriversOnMap.replace(this.currentActiveRoutes);
    }
  }

  @action updateSelectedDriverRouteId(routeId) {
    this.selectedDriverRouteId = routeId;
  }
  @action addUnreadMessages(unreadMessages) {
    //add unReadMessages for showing in driver's grid count
    const driverUnreadMessageCount = unreadMessages.map(
      (driverUnreadMessage) => ({
        driverId: driverUnreadMessage.driverId,
        unreadMessageCount: driverUnreadMessage.messages.length,
      }),
    );
    this.driverUnReadMessageCountList.replace(driverUnreadMessageCount);
    this.addUnreadMessagesForNotifications(unreadMessages);
  }

  addUnreadMessagesForNotifications = (unreadMessages) => {
    const allDriversSortedUnReadMessages = unreadMessages
      .reduce((flattenUnReadMessages, unReadMessage) => {
        if (unReadMessage.messages.length === 0) {
          return flattenUnReadMessages;
        }
        return [...flattenUnReadMessages, ...unReadMessage.messages];
      }, [])
      .sort((a, b) => new Date(a.messageDateTs) - new Date(b.messageDateTs));
    this.addDispatchedDriverNotification(allDriversSortedUnReadMessages);
  }

  getUnreadMessageCounts(driverId) {
    const messageCount = this.driverUnReadMessageCountList.find(
      (count) => count.driverId === driverId
    );
    return messageCount?.unreadMessageCount || 0;
  }

  updateUnreadMessageCountList = (driverId, unReadCount) => {
    const driverUnReadMessageCountList = this.driverUnReadMessageCountList.map(
      (messageCount) => {
        if (messageCount.driverId === driverId) {
          messageCount.unreadMessageCount = unReadCount;
        }
        return messageCount;
      }
    );
    this.driverUnReadMessageCountList.replace(driverUnReadMessageCountList);
  };

  @computed get returnCodes() {
    const { clientCodes } = this.staticClientData || {};
    if (!clientCodes?.returnCodeList) {
      return [];
    }
    const showCode = this.showReturnReasonCode;
    return clientCodes.returnCodeList.map((data) => ({
      value: data.code,
      label: showCode ? `(${data.code}) ${data.description}` : data.description,
      isException: data.isException,
      displayReturnToTerminal: data.displayReturnToTerminal,
      isArrivalDepartureTimeRequired: data.isArrivalDepartureTimeRequired,
    }));
  }

  @computed get deliveryStatuses() {
    const { clientCodes } = this.staticClientData || {};
    if (!clientCodes?.deliveryStatusList) {
      return [];
    }
    return clientCodes.deliveryStatusList.map((data) => ({
      value: data.statusCode,
      labelText: data.status,
    }));
  }

  @computed get exceptions() {
    const { clientCodes } = this.staticClientData || {};
    if (!clientCodes?.exceptionList) {
      return [];
    }
    return clientCodes.exceptionList.map((data) => ({
      value: data.code,
      labelText: data.description,
      isException: data.isException,
    }));
  }

  @computed get limitedAccessList() {
    const { clientCodes } = this.staticClientData || {};
    if (!clientCodes?.limitedAccessList) {
      return [];
    }
    return sortBy(clientCodes.limitedAccessList, item => Number(item.sort)).map((data) => ({
      value: data.code,
      label: data.description,
    }));
  }

  @computed get packageCodes() {
    const { packageCodes } = this.staticClientData || {};
    if (packageCodes?.length > 0) {
      return packageCodes.map((data) => ({
        value: data.code,
        label: data.description,
      }));
    }
    return [];
  }

  // using this to show package types along with package code in Create/Edit pickup shipment grid dropdown.
  @computed get packageCodesWithSuffix() {
    const { packageCodes } = this.staticClientData;
    if (packageCodes.length > 0) {
      return packageCodes.map((data) => ({
        value: data.code,
        label: data.description,
        labelSuffix : `(${data.code})`
      }));
    }
    return [];
  }

  //TODO : Remove isChecked props from here
  @computed get deliveryAccessorials() {
    const accessorialList = this.deliveryAccessorialList;
    return accessorialList.map((data) => ({
      name: data.code,
      labelText: data.description,
      isChecked: false,
    }));
  }

  @computed get pickupAccessorials() {
    const accessorialList = this.pickupAccessorialList;
    return accessorialList.map((data) => ({
      name: data.code,
      labelText: data.description,
    }));
  }

  @action getDeliveryStatusLabelFromCode(deliveryCode) {
    const { clientCodes } = this.staticClientData || {};
    return clientCodes?.deliveryStatusList?.find(
      (deliveryStatus) => deliveryStatus.statusCode === deliveryCode
    )?.status;
  }

  @action getExceptionDetails(exceptionCode) {
    const { clientCodes } = this.staticClientData || {};
    return clientCodes?.exceptionList?.find(
      (exception) => exception.code === exceptionCode
    );
  }

  @action getReturnDetails(returnCode) {
    return (
      this.staticClientData?.clientCodes?.returnCodeList?.find(
        (returnType) => returnType.code === returnCode
      ) ?? null
    );
  }

  @computed get priorityAllShipmentList() {
    return (
      this.staticClientData?.clientCodes?.serviceLevelList.map(
        (data) => data.serviceLevel
      ) || []
    );
  }

  @computed get priorityShipmentList() {
    return this.staticClientData?.clientCodes?.serviceLevelList.reduce(
      (priorityShipmentList, data) => {
        return data.isVisible
          ? [...priorityShipmentList, data.serviceLevel]
          : [...priorityShipmentList];
      },
      []
    )|| [];
  }

  @computed get priorityShipmentOptions() {
    return (
      this.staticClientData?.clientCodes?.serviceLevelList.reduce(
        (priorityShipmentOptions, data) => {
          return data.isVisible
            ? [
                {
                  value: data.serviceLevel,
                  label: data.serviceLevel,
                },
                ...priorityShipmentOptions,
              ]
            : [...priorityShipmentOptions];
        },
        [{ value: "", label: "None" }]
      ) || []
    );
  }

  @computed get pickupListForOptimization() {
    return this.pickupsList
      .filter(
        (pickup) =>
          !pickup.isCompleted &&
          !pickup.isVoided &&
          !pickup.isMissed &&
          isTodaysPickup(pickup.pickupDate, this.terminalOffset) &&
          isPickupGroupHeader(pickup)
      )
      .sort((a, b) =>
        this.selectedPickupGroupIds.includes(a.pickupId.toLowerCase()) ? -1 : 0
      ); // return all Assigned and Unassigned pickups for optimization
  }


  @action getCurrentDriver(driverId) {
    return this.currentActiveRoutes.find(
      (driver) => driver.driverId.toUpperCase() === driverId.toUpperCase()
    );
  }

  @action getPickupGroupHeader(groupId) {
    return this.pickupsList.find(
      (pickup) => 
        pickup.groupId === groupId && 
        isPickupGroupHeader(pickup)
    );
  }

  @computed get selectedPickupGroupIds() {
    // Returns unique group IDs of all selected pickups
    const groupIds = new Set();
    this.pickupsList.forEach((pickup) => {
      if (pickup.pickupId && this.selectedPickupIdList.includes(pickup.pickupId.toLowerCase())) {
        groupIds.add(pickup.groupId);
      }
    });
    
    return Array.from(groupIds);
  }

  @computed get groupHeaderPickupIdsForSelectedPickups() {
    return this.pickupsList
      .filter(pickup => 
        isPickupGroupHeader(pickup) && 
        this.selectedPickupGroupIds.includes(pickup.groupId)
      )
      .map(groupHeader => groupHeader.pickupId.toLowerCase());
  }

  @computed get unreadDriverNotifications() {
    const driverIds = this.dispatchedDrivers.map((driver) =>
      driver.driverId.toUpperCase()
    );
    return this.dispatchedDriverNotifications
      .filter(
        (msg) => driverIds.includes(msg.sender.toUpperCase())
      )
      .map((msg) => {
        const driver = this.getCurrentDriver(msg.sender);
        return { ...msg, driver };
      });
  }

  // we need this in Create Pickup Shipments grid where "None" will be first option in "Service Level" dropdown.
  @computed get serviceLevelShipmentOptions() {
    return (
      this.staticClientData?.clientCodes?.serviceLevelList.reduce(
        (priorityShipmentOptions, data) => {
          return data.isVisible
            ? [
                ...priorityShipmentOptions,
                {
                  value: data.serviceLevel,
                  label: data.serviceLevel,
                },
              ]
            : [...priorityShipmentOptions];
        },
        [{ value: "", label: "None" }]
      ) || []
    );
  }

  @action updateDuplicatePickups(duplicatePickups) {
    this.duplicatePickups.replace(duplicatePickups);
  }

  @action updateClosingSoonPickups() {
    const pickupIdToClosingTimeMapping = this.pickupsList.reduce((acc, pickup) => {
      const isPickupActive = isActivePickup(pickup, this.terminalOffset);
      const closingTime = isPickupActive
        ? getPickupTimeToCloseInMins({
            closeTime: pickup.pickupCloseTime,
            terminalOffset: this.terminalOffset,
            closingSoonWarningTimeLimit: this.closingSoonWarningTimeLimit
          })
        : null;
      if (closingTime !== null) {
        acc[pickup.pickupId.toUpperCase()] = closingTime;
      }
      return acc;
    }, {});
    this.pickupIdToClosingTimeMapping = { ...pickupIdToClosingTimeMapping };
  }

  @action getClosingSoonPickupTime(pickupId){
    return this.pickupIdToClosingTimeMapping[pickupId?.toUpperCase?.()] ?? null;
  }
  
  @computed get dispatchedDriversListForOptimization() {
    return this.dispatchedDrivers.map((driver) => ({
      label: driver.driverName,
      value: driver.driverId,
      labelSuffix: `(${driver.driverNumber})`,
    }));
  }

  @action setMovePickupTerminalList = (movePickupTerminalList) => {
    this.movePickupTerminalList.replace(movePickupTerminalList);
  };

  @computed get dispatchableDrivers(){
    const dispatchedDriverIds = this.dispatchedDrivers.map(
      (driver) => driver.driverId
    );
    return this.availableDriverList.filter(driver => !dispatchedDriverIds.includes(driver.driverId))
  }

  @action setSelectedStopInKPI(stopId) {
    this.selectedStopInKPI = stopId;
  }

  @action updateGeofenceArrivalDeparture({  driverId,stopId,geoFenceArrivalTime,geoFenceDepartureTime }){
    this.geofenceWindow = {
      ...this.geofenceWindow,
      [driverId.toUpperCase()]: {
        ...(this.geofenceWindow[driverId.toUpperCase()] ?? {}),
        [stopId.toUpperCase()]:{
          geoFenceArrivalTime,
          geoFenceDepartureTime,
        }
      }
    };
  }

  @action updateDriverGateinGateout({ driverId, geoFenceArrivalTime, geoFenceDepartureTime, eventType }) {
    const updatedDriversList = this.dispatchedDrivers.map((driver) => {
      if (driver.driverId.toUpperCase() !== driverId.toUpperCase()) {
        return driver;
      }
    
      const updatedDriverActiveRoutes = (driver.dispatchRoutes || []).map((route) => {
        if (route.isActiveRoute) {
          return { ...route, routeStatus: eventType };
        }
        return route;
      });
    
      return {
        ...driver,
        gateInTime: geoFenceArrivalTime,
        gateOutTime: geoFenceDepartureTime,
        driverStatus: eventType,
        dispatchRoutes: updatedDriverActiveRoutes,
      };
    });
    
    this.dispatchedDrivers.replace(updatedDriversList);
  }

  @action getGeofenceWindow(driverId, stopId) {
    return this.geofenceWindow[driverId.toUpperCase()]?.[stopId.toUpperCase()] ?? {
      geoFenceArrivalTime: "",
      geoFenceDepartureTime: ""
    }
  }

  @action removeDriverGeofence(driverId) {
    delete this.geofenceWindow[driverId.toUpperCase()]
  }

  @action getExcessiveLengthMaxLimit(trailerLength) {
    const fixedLengthForEL = this.staticClientData?.clientCodes?.useFixedLengthForEL;
    return fixedLengthForEL || trailerLength;
  };

  @computed get maxAllowedTrailerCount() {
    return this.staticClientData?.clientCodes?.maxAllowedTrailerCount ?? appConstants.DEFAULT_MAX_ALLOWED_TRAILERS_COUNT;
  };
}

export default new DispatchStore();
